import React from 'react';
import { useLocation } from 'react-router-dom';
import { Button, ChevronLeftIcon } from '../../components/ui';
import { getLocationReturnPath, useNavigateBack } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import { CREW_REPORT_CATALOG } from './reportCatalog';
import { CREW_STATISTICS_CATALOG } from './statisticsCatalog';

const STATISTIC_PATHS = new Set(
  CREW_STATISTICS_CATALOG.flatMap((group) => group.items.map((item) => item.path)),
);

const REPORT_PATHS = new Set(
  CREW_REPORT_CATALOG.flatMap((group) => group.reports.map((report) => report.path)),
);

/** Sensible fallback when browser history cannot go back. */
export function getCrewBackFallback(pathname: string): string {
  if (pathname === '/crew/statistics' || pathname === '/crew/reports') return '/crew';
  if (pathname.startsWith('/crew/reports/') || REPORT_PATHS.has(pathname)) return '/crew/reports';
  if (pathname.startsWith('/crew/statistics/') || STATISTIC_PATHS.has(pathname)) {
    return '/crew/statistics';
  }
  if (/^\/crew\/\d+/.test(pathname)) return '/crew';
  if (pathname.startsWith('/crew/') && pathname !== '/crew') return '/crew';
  return '/crew';
}

type CrewBackButtonProps = {
  /** Override fallback when history cannot go back. */
  fallbackPath?: string;
  className?: string;
  title?: string;
};

/** Icon-only back control — prefers previous history entry / `state.from`, else a crew fallback route. */
export const CrewBackButton: React.FC<CrewBackButtonProps> = ({
  fallbackPath,
  className,
  title = 'Back',
}) => {
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const goBack = useNavigateBack(
    fallbackPath ?? getCrewBackFallback(location.pathname),
    returnPath,
  );

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={title}
      aria-label={title}
      className={cn('shrink-0 self-center', className)}
      onClick={goBack}
    >
      <ChevronLeftIcon className="h-5 w-5" />
    </Button>
  );
};

export default CrewBackButton;
